import type { Dict } from '../types'

/**
 * Dutch. Vocabulary and tone follow MOS MOSH's own Dutch trade-in copy (Core
 * `brandLocalization.tradein2`): informal "je/jouw", inruil/inruilen for the trade-in, items for the
 * items, voucher for the reward, afgiftepunt, verzendlabel, waslabel. Translated best-effort —
 * please proofread.
 */
export const nl: Dict = {
  languageName: 'Nederlands',

  common: {
    points: 'punten',
  },

  // Subject-word lexicon (see en.ts). Dutch defaults for clothing (kleding = de-word, collective
  // singular). A brand supplies the full phrase per form, so its own article travels with the word.
  //
  // Dutch is the easiest of our languages for this: "je", "een" and predicative adjectives ("klaar",
  // "geldig") are all gender-invariant, so no string here is bound to voucher being a de-word. The
  // one shape assumption is that {reward} compounds ("vouchercode") — a brand whose term is a phrase
  // needs a dictionary override for confirm.copyCode / confirm.codeCopied.
  subject: {
    noun: 'kleding',
    nounPoss: 'je kleding',
    nounDef: 'de kleding',
    reward: 'voucher',
    rewardPoss: 'je voucher',
    rewardDef: 'de voucher',
    rewardPlural: 'vouchers',
  },

  auth: {
    signingIn: 'Je wordt ingelogd…',
    stillWorking: 'Nog even bezig — we halen je eerdere bestellingen op. Dit kan een moment duren.',
    loadingItems: 'Je items worden geladen…',
    errorTitle: 'We konden je inruil niet starten',
    errorGeneric: 'Er ging iets mis bij het inloggen.',
    missingShop: 'App is verkeerd geconfigureerd — shopId ontbreekt.',
    needSignIn: 'Je moet inloggen voordat je kunt inruilen.',
    signInFailed: 'Inloggen mislukt. Probeer het opnieuw.',
    linkInvalid: 'Deze inloglink werkt niet meer. Het profiel waar hij bij hoort is mogelijk verwijderd. Ga terug naar de startpagina om opnieuw in te loggen.',
    backToFrontPage: 'Terug naar de startpagina',
  },

  config: {
    title: 'Configuratiefout',
    missingShop: 'Deze inruil mist de winkelconfiguratie (shopId). Neem contact op met de winkel.',
  },

  login: {
    title: 'Geef {nounPoss} een tweede leven',
    subtitle: 'Log in om je inruil te starten en maak van items die je niet meer draagt een {reward}.',
    emailLabel: 'E-mail',
    emailPlaceholder: 'jij@voorbeeld.nl',
    start: 'Verder',
    working: 'Even geduld…',
    emailSentTitle: 'Controleer je e-mail',
    emailSentBody: 'We hebben een inloglink gestuurd naar {email}. Open die op dit apparaat om verder te gaan.',
    phoneLabel: 'Telefoonnummer',
    phonePlaceholder: '06 12 34 56 78',
    sendCode: 'Code verzenden',
    codeLabel: 'Verificatiecode',
    codePlaceholder: '6-cijferige code',
    verify: 'Verifiëren en verder',
    changePhone: 'Gebruik een ander nummer',
    back: 'Terug',
    methodSms: 'Inloggen met sms',
    methodSmsDesc: 'We sturen je een code via sms — als we je telefoonnummer hebben geregistreerd.',
    methodEmail: 'Inloggen met e-mail',
    methodEmailDesc: 'We sturen een inloglink naar je inbox.',
    or: 'of',
    wholesale: 'In de winkel gekocht of cadeau gekregen?',
    emailInvalid: 'Voer een geldig e-mailadres in.',
    phoneInvalid: 'Voer een geldig telefoonnummer in.',
    codeInvalid: 'Die code werkte niet. Probeer het opnieuw.',
    failed: 'Er ging iets mis. Probeer het opnieuw.',
    step1Title: 'Vind je items',
    step1Body: 'Zoek of maak een foto van wat je niet meer draagt.',
    step2Title: 'Stuur ze op',
    step2Body: 'Pak alles in één pakket met je prepaid verzendlabel.',
    step3Title: 'Ontvang {rewardPoss}',
    step3Body: {
      received: 'Ontvang {rewardPoss} zodra we je items hebben ontvangen.',
      handin: 'Ontvang {rewardPoss} zodra je pakket bij het afgiftepunt is gescand.',
      instant: 'Ontvang {rewardPoss} direct — stuur ons daarna je items.',
    },
  },

  country: {
    title: 'Je land wordt mogelijk niet ondersteund',
    body: 'Het lijkt erop dat we geen vervoerder hebben die een inruil vanuit jouw land naar ons magazijn kan versturen. Je kunt gerust verdergaan, maar we kunnen de inzameling mogelijk niet afronden.',
    dismiss: 'Toch verdergaan',
  },

  menu: {
    open: 'Menu',
    language: 'Taal',
    myTradeIns: 'Mijn inruilen',
    howItWorks: 'Zo werkt het',
    noneYet: 'Nog geen',
    logout: 'Uitloggen',
    soon: 'Binnenkort',
  },

  // Voucherregels, hergebruikt door de pagina "Mijn inruilen" voor de vouchersectie.
  rewards: {
    activeHeading: 'Beschikbaar',
    spentHeading: 'Besteed',
    expires: 'Verloopt op {date}',
    active: 'Actief',
    spentBadge: 'Besteed',
  },
  // Accountpagina "Mijn inruilen": eerst de vouchers, dan de inruilhistorie + detaildialoog.
  myTradeIns: {
    title: 'Mijn inruilen',
    subtitle: 'Je inruilen en {rewardPlural}.',
    back: 'Terug naar de inruil',
    loading: 'Je inruilen worden geladen…',
    empty: 'Je hebt nog geen inruilen of {rewardPlural}.',
    historyHeading: 'Je inruilen',
    tradeInPrefix: 'Inruil',
    itemsCount: '{count} items',
    statusReceived: 'Ontvangen',
    statusOnItsWay: 'Onderweg naar ons',
    statusAwaiting: 'Wacht op je pakket',
    detailTitle: 'Inruildetails',
    nextHeading: 'Wat gebeurt er nu',
    nextReceived: 'We hebben je items ontvangen — {rewardPoss} is onderweg.',
    nextTransit:
      'Je pakket is onderweg naar ons. We mailen je zodra we je items hebben ontvangen en gecontroleerd.',
    nextPickup: 'Breng je items naar onze winkel, dan regelen wij de rest.',
    nextShip: 'Pak je items in en stuur ze naar ons op met de gegevens hieronder.',
    itemsHeading: 'Items in deze inruil',
    labelUnavailable: 'Het verzendlabel is niet meer beschikbaar.',
    close: 'Sluiten',
  },

  steps: {
    items: 'Items',
    story: 'Jouw verhaal',
    details: 'Gegevens',
    shipping: 'Verzending',
    confirm: 'Bevestigen',
  },

  rail: {
    title: 'Je inruil',
    itemCount: '{count} item',
    itemCountPlural: '{count} items',
    empty: 'Nog geen items — voeg je eerste item toe en zie de waarde oplopen.',
    loading: 'Je inruil wordt geladen…',
    estimatedReward: 'Totale waarde',
    rewardNoteConfirmed: {
      received: 'Bevestigd zodra we je items ontvangen',
      handin: 'Bevestigd zodra je je pakket afgeeft',
      instant: 'Uitgegeven zodra je je inruil bevestigt',
    },
    rewardNoteAdd: 'Voeg items toe om de waarde te zien',
    addedManually: 'Handmatig toegevoegd',
    maxExceeded: 'Deze inruil overschrijdt het maximum van {amount}. Verwijder een item om verder te gaan.',
    remove: '{name} verwijderen',
    back: 'Terug',
    continue: 'Verder',
    confirmTradeIn: 'Inruil bevestigen',
    done: 'Klaar',
  },

  items: {
    firstTitle: 'Laten we je eerste item zoeken',
    firstSubtitle: 'Maak een foto of zoek in de catalogus.',
    anotherTitle: 'Nog een item toevoegen?',
    anotherSubtitle: 'Bekijk alles nog even voordat je verdergaat.',
    selectTitle: 'Selecteer items om in te ruilen',
    selectSubtitle: 'Uit je eerdere bestellingen — tik om toe te voegen.',
    manualTitle: 'Een item handmatig toevoegen',
    manualSubtitle: 'Voor items die je ergens anders hebt gekocht.',
    estReward: 'Waarde',
    bought: 'Gekocht op {date}',
    addManually: 'Handmatig toevoegen',
    boughtElsewhere: 'Ergens anders gekocht?',
    addedCount: '{count} toegevoegd',
    nothingTitle: 'Niets om in te ruilen',
    nothingBody:
      'We konden op je account geen items vinden die in aanmerking komen voor inruil. Inruilen kan hier alleen met items die je bij ons hebt gekocht — zodra je een geschikte bestelling hebt, verschijnt die hier.',
  },

  intro: {
    title: 'Zo werkt inruilen',
    lead: 'Ruil {noun} van {brand} in die je niet meer draagt — en ontvang {rewardPoss} om te besteden aan nieuwe of secondhand items.',
    step1Title: 'Vind je items',
    step1Body:
      'Maak een foto van wat je niet meer draagt, of zoek in onze catalogus. Voeg zoveel items toe als je wilt.',
    step2Title: 'Stuur ze op',
    step2Body: 'Pak alles in één pakket met je prepaid verzendlabel — de verzending is voor onze rekening.',
    step3Title: 'Ontvang {rewardPoss}',
    step3Body: {
      received: 'Zodra we je items hebben ontvangen en gecontroleerd, mailen we {rewardPoss}.',
      handin: 'Zodra je pakket bij afgifte wordt gescand, mailen we {rewardPoss}.',
      instant: '{rewardPoss|cap} wordt gemaild zodra je bevestigt — daarna stuur je ons je items.',
    },
    cta: 'Duidelijk',
    close: 'Sluiten',
  },

  finder: {
    search: 'Zoek in catalogus',
    photo: 'Toevoegen met foto',
    searchTitle: 'Zoek in de catalogus',
    searchSub: 'Op SKU, EAN of productnaam.',
    searchPlaceholder: 'Zoek op SKU, EAN of productnaam',
    carelabelHint: 'Tip: je vindt de SKU of het stijlnummer op het waslabel van je item.',
    searching: 'Zoeken…',
    searchFailed: 'Zoeken mislukt — probeer het opnieuw.',
    noMatches: 'Geen resultaten voor ‘{query}’.',
    unavailable: 'Zoeken in de catalogus is niet beschikbaar voor deze winkel.',
    estReward: 'Waarde',
    notEligible: 'Niet geschikt',
    add: 'Toevoegen',
    adding: 'Toevoegen…',
    added: '{name} toegevoegd · +{amount}',
    backToOrders: 'Terug naar je bestellingen',
    showMore: 'Meer resultaten tonen',
    loadingMore: 'Laden…',
  },

  photo: {
    dropTitle: 'Maak een foto van je item',
    dropHint: 'We zoeken vergelijkbare items in onze catalogus.',
    or: 'of',
    wayDevice: 'Foto maken of uploaden',
    wayPhone: 'Gebruik de camera van je telefoon',
    qrAgainTitle: 'Probeer een andere foto',
    qrAgainHint: 'Maak een nieuwe foto met je telefoon, dan zoeken we automatisch opnieuw.',
    analysingTitle: 'De beste matches zoeken…',
    analysingHint: 'Je foto wordt vergeleken met onze catalogus',
    uploadingTitle: 'Je foto wordt geüpload…',
    uploadingHint: 'Een moment',
    yourPhoto: 'Jouw foto',
    matchedTo: 'Gematcht met onze catalogus',
    replace: 'Vervangen',
    headingConfident: 'Lijkt een van deze op jouw item?',
    headingCategory: 'Beste matches in {category}',
    noMatchHeading: 'We konden geen goede match vinden',
    noMatchBody:
      'Geen van onze catalogusitems kwam goed overeen. Probeer een andere foto, of zoek in plaats daarvan op naam.',
    // Distinct from "no match": the search never ran, so the item may well be in the catalogue.
    notSearched: 'Nog niet gezocht',
    searchFailedHeading: 'We konden nu niet zoeken',
    searchFailedBody:
      'Er ging iets mis aan onze kant, dus we hebben je foto nog niet vergeleken. Je foto is opgeslagen — probeer het opnieuw.',
    tryAgain: 'Opnieuw proberen',
    refine: 'Filteren op categorie',
    clear: 'Wissen',
    noneOfThese: 'Geen van deze',
    // "Toevoegen per categorie", bereikbaar via de plaatshoudertegel in de fotoresultaten.
    categoryCardTitle: 'Kun je je item niet vinden?',
    categoryCardHint: 'Kies in plaats daarvan een categorie',
    categoryPickTitle: 'Wat voor item is het?',
    categoryPickHint: 'We gebruiken de foto die je zojuist hebt gemaakt.',
    categoryValue: 'Waarde',
    categoryConfirm: 'Toevoegen aan inruil',
    categoryBack: 'Terug',
    uploadAnother: 'Nog een foto uploaden',
    bestMatch: 'Beste match',
    uploadFailed: 'Er ging iets mis — probeer het opnieuw.',
  },

  match: {
    strong: 'Sterke match',
    colour: 'Kleur: {color}',
    estimatedReward: 'Waarde',
    backToMatches: 'Terug naar de matches',
    backToResults: 'Terug naar de resultaten',
    confirm: 'Ja, dit is mijn item',
  },

  details: {
    title: 'Je gegevens',
    subtitle: 'Zodat we je prepaid verzendlabel kunnen maken en {rewardPoss} kunnen versturen.',
    contact: 'Contact',
    address: 'Adres',
    fullName: 'Volledige naam',
    email: 'E-mail',
    country: 'Land',
    streetAddress: 'Straat en huisnummer',
    postalCode: 'Postcode',
    city: 'Plaats',
    prefix: 'Landcode',
    phone: 'Telefoon',
    emailLocked: 'Je e-mailadres is gekoppeld aan je account en kan hier niet worden gewijzigd.',
    consentPre: 'Ik accepteer de ',
    consentLink: 'inruilvoorwaarden',
    consentPost: ' en bevestig dat de items van mij zijn om in te ruilen.',
    required: 'Dit veld is verplicht.',
    emailInvalid: 'Voer een geldig e-mailadres in.',
    phoneInvalid: 'Voer een geldig telefoonnummer in.',
    zipInvalid: 'Voer een geldige postcode in',
    zipFormat: 'formaat: {format}',
    loading: 'Je gegevens worden geladen…',
    saveFailed: 'We konden je gegevens niet opslaan. Probeer het opnieuw.',
  },

  terms: {
    title: 'Voorwaarden voor het inruilen van {noun}',
    // {brand} is de merknaam van de winkel (config.brandName), of een generieke fallback.
    introLead:
      'Als onderdeel van ons secondhand-initiatief kun je {nounPoss} van {brand} inruilen voor een {reward}.',
    introAccept:
      'Door {nounPoss} van {brand} op te sturen, ga je ermee akkoord dat een {reward} alleen wordt uitgegeven voor {noun} die aan de onderstaande voorwaarden voldoet, en dat {noun} die niet aan de eisen voldoet NIET aan je wordt teruggestuurd.',
    introDonate:
      '{noun|cap} die niet aan onze eisen voldoet, wordt gedoneerd aan een goed doel of gegeven aan bedrijven die de items of hun materialen kunnen hergebruiken.',
    requirementsTitle: 'Eisen aan {nounDef} die je inlevert',
    requirementsBody:
      'We accepteren {noun} van {brand} die schoon is en waarvan alle originele labels intact zijn — zowel het neklabel als het waslabel (wasvoorschriften e.d.).',
    howToTitle: 'Zo lever je {nounPoss} in',
    howToRegister:
      'Je registreert de {brand}-producten die je wilt inleveren. Tijdens de registratie zie je de waarde van elk product — het bedrag dat je als {reward} ontvangt in ruil voor {nounPoss}.',
    howToAdjust:
      'Als wat we ontvangen niet overeenkomt met wat je hebt geregistreerd, corrigeren we dit naar het juiste product en passen we je totale waarde daarop aan. Door {nounPoss} op te sturen, ga je akkoord met deze aanpassing.',
    howToLabel:
      'Zodra je je registratie hebt afgerond, ontvang je een verzendlabel of QR-code om {nounPoss} op te sturen. Dit verzendlabel is vooruitbetaald door {brand}.',
    howToFinal:
      'Zodra je {nounPoss} hebt ingeleverd, kun je je inzending niet meer ongedaan maken en sturen we je producten niet terug.',
    voucherTitle: '{reward|cap}',
    voucherIssue: {
      received:
        'Je ontvangt een {reward} zodra we hebben bevestigd dat {nounPoss} aan de bovenstaande eisen voldoet. {rewardDef|cap} wordt verstuurd naar het e-mailadres dat je hebt opgegeven.',
      handin:
        'Je ontvangt een {reward} zodra je pakket bij het afgiftepunt is geregistreerd. {rewardDef|cap} wordt verstuurd naar het e-mailadres dat je hebt opgegeven. Als {nounPoss} niet aan de bovenstaande eisen voldoet, kan {rewardDef} worden ingetrokken.',
      instant:
        'Je ontvangt een {reward} zodra je je inruil registreert. {rewardDef|cap} wordt verstuurd naar het e-mailadres dat je hebt opgegeven. Als {nounPoss} niet naar ons wordt opgestuurd, of niet aan de bovenstaande eisen voldoet, kan {rewardDef} worden ingetrokken.',
    },
    voucherValidity:
      '{rewardDef|cap} is 3 jaar geldig vanaf de uitgifte en kan worden gebruikt voor online aankopen bij {brand}. {rewardDef|cap} kan niet worden ingewisseld voor contant geld.',
    closing: 'We kijken ernaar uit om {nounPoss} te ontvangen.',
    brandFallback: 'het merk',
    close: 'Sluiten',
    accept: 'Accepteren en verder',
  },

  shipping: {
    title: 'Hoe stuur je het op?',
    subtitle: 'Pak je {count} item in — één label voor alles.',
    subtitlePlural: 'Pak je {count} items in — één label voor alles.',
    loading: 'Verzendopties worden geladen…',
    none: 'Er is nog geen verzendservice beschikbaar voor je adres.',
    maxExceeded: 'Deze inruil overschrijdt de maximale waarde. Verwijder een item en probeer het opnieuw.',
    createFailed: 'We konden je inruil niet aanmaken. Probeer het opnieuw.',
    desc_gls: 'Afgeven bij een GLS-pakketpunt. Gratis, geprint label.',
    desc_dhl: 'Afgeven bij een DHL-pakketpunt. Gratis, geprint label.',
    desc_dao: 'Een koerier haalt het pakket bij je op.',
    desc_shipmondo: 'Afgeven met een geprint label.',
    desc_pickup: 'Afgeven in onze fysieke winkel.',
    desc_postnord: 'Afgeven bij elk PostNord-pakketpunt.',
  },

  story: {
    title: 'Vertel ons je verhaal',
    subtitle: 'Optioneel — we horen graag over je items.',
    placeholder: 'Vertel over dit item — waar je het droeg, waarom je er zo van hield…',
    consent:
      'Jullie mogen mijn verhaal (anoniem) delen om andere klanten te inspireren hun {noun} een tweede leven te geven.',
  },

  confirm: {
    topbarTitle: 'Bevestigen',
    heroTitle: 'Je bent helemaal klaar',
    heroBody: {
      received: 'Je inruil is geregistreerd. Volg de drie stappen hieronder en {rewardPoss} is onderweg.',
      handin:
        'Je inruil is geregistreerd. Volg de drie stappen hieronder — {rewardPoss} wordt uitgegeven zodra je je pakket afgeeft.',
      instant:
        'Je inruil is geregistreerd en {rewardPoss} is onderweg. Volg gewoon de drie stappen hieronder om ons je items te sturen.',
    },
    packTitle: 'Pak je {count} item in',
    packTitlePlural: 'Pak je {count} items in',
    packBody: 'Doe alles in één pakket. Speciale verpakking is niet nodig — hergebruik een doos of tas.',
    pickupPackBody: 'Pak je items in en breng ze naar onze fysieke winkel — een verzendlabel is niet nodig.',
    shippingCode: 'Verzendcode',
    generatingLabel: 'Je verzendlabel wordt aangemaakt…',
    labelFailed: 'We konden je label niet aanmaken. Neem contact op met de klantenservice.',
    showQr: 'QR-code tonen',
    showLabel: 'Verzendlabel tonen',
    dropTitle: 'Geef het af',
    dropBody: 'Toon de code of het label bij het afgiftepunt van je vervoerder. ',
    findShop: 'Zoek het dichtstbijzijnde afgiftepunt →',
    pickupTitle: 'Afgeven in de winkel',
    pickupBody: 'Breng je items naar onze winkel, dan regelen wij de rest.',
    rewardTitle: 'Ontvang {rewardPoss}',
    // Gesplitst rond het bedrag, dat ConfirmPage tussen beide helften in <b> rendert ("…mailen we je
    // [€ 50] {reward}"). "je" is in het Nederlands onzijdig én mannelijk/vrouwelijk, dus anders dan in
    // het Deens werken deze drie regels ongeacht het woord dat de brand kiest.
    rewardBodyPre: {
      received: 'Zodra we je pakket ontvangen, mailen we je ',
      handin: 'Zodra je pakket bij afgifte wordt gescand, mailen we je ',
      instant: 'Je ',
    },
    rewardBodyPost: {
      received: ' {reward} binnen 2 werkdagen.',
      handin: ' {reward}. Houd er rekening mee dat het een paar uur kan duren voordat de track & trace van de afgifte ons bereikt.',
      instant: ' {reward} is al onderweg naar je inbox — stuur ons je items via de stappen hierboven.',
    },
    // INSTANT-strategie: de vouchercode wordt hier getoond zodra Core deze heeft uitgegeven.
    voucherReadyTitle: '{rewardPoss|cap} is klaar',
    voucherIssuing: '{rewardPoss|cap} wordt aangemaakt…',
    voucherExpires: 'verloopt op {date}',
    copyCode: '{reward|cap}code kopiëren',
    codeCopied: '{reward|cap}code gekopieerd',
    startAnother: 'Nog een inruil starten',
    backToShop: 'Terug naar de winkel',
  },

  dropoff: {
    title: 'Dichtstbijzijnde afgiftepunten',
    qrSupported: 'Ondersteunt afgifte met QR-code — een geprint label is niet nodig.',
    qrNotSupported: 'Vereist een geprint label.',
  },
}
